import React, { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Seite from "../components/Seite";

const leerReise = {
  ziel: "", von: "", bis: "",
  adresse1: "", adresse2: "", adresse3: "", adresse4: "", adresse5: "", adresse6: "",
  verkehrsmittel: "flug",
  hinfahrt: "", rueckfahrt: "",
  hotel: "", notizen: "",
  screenshots: [],
};

const mapsLink = (adresse) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresse)}`;

function ReiseFormular({ daten, setDaten, onSubmit, onAbbrechen, user, reiseId }) {
  const [ordnerId] = useState(() => reiseId || crypto.randomUUID());
  const [hochladen, setHochladen] = useState(false);

  const screenshotsHochladen = async (dateien) => {
    if (dateien.length === 0) return;
    setHochladen(true);
    try {
      const neue = [];
      for (const datei of dateien) {
        const pfad = `reisen/${user.uid}/${ordnerId}/${Date.now()}_${datei.name}`;
        const storageRef = ref(storage, pfad);
        await uploadBytes(storageRef, datei);
        const url = await getDownloadURL(storageRef);
        neue.push({ url, pfad });
      }
      setDaten((d) => ({ ...d, screenshots: [...(d.screenshots || []), ...neue] }));
    } catch (err) { alert("Fehler beim Hochladen: " + err.message); }
    setHochladen(false);
  };

  const dateiAuswahl = (e) => {
    const dateien = Array.from(e.target.files || []);
    e.target.value = "";
    screenshotsHochladen(dateien);
  };

  // Erlaubt, Screenshots direkt aus der Zwischenablage einzufügen (Strg+V), z.B. nach
  // einem Screenshot-Tool – ohne Umweg über "Datei speichern" + Dateiauswahl.
  const screenshotEinfuegen = (e) => {
    const bilder = Array.from(e.clipboardData?.items || [])
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (bilder.length === 0) return; // z.B. eingefügter Text – normal weiterverarbeiten
    e.preventDefault();
    const benannt = bilder.map((b, i) => new File([b], `screenshot_${Date.now()}_${i}.png`, { type: b.type }));
    screenshotsHochladen(benannt);
  };

  const screenshotLoeschen = async (index) => {
    const foto = (daten.screenshots || [])[index];
    setDaten((d) => ({ ...d, screenshots: d.screenshots.filter((_, i) => i !== index) }));
    if (foto?.pfad) {
      try { await deleteObject(ref(storage, foto.pfad)); } catch { /* ggf. schon gelöscht */ }
    }
  };

  return (
    <form onSubmit={onSubmit} onPaste={screenshotEinfuegen} style={styles.formular}>
      <input style={styles.input} placeholder="Reiseziel *" value={daten.ziel} onChange={(e) => setDaten({ ...daten, ziel: e.target.value })} required />
      <div style={styles.zeile}>
        <input style={styles.input} type="date" placeholder="Von" value={daten.von} onChange={(e) => setDaten({ ...daten, von: e.target.value })} />
        <input style={styles.input} type="date" placeholder="Bis" value={daten.bis} onChange={(e) => setDaten({ ...daten, bis: e.target.value })} />
      </div>
      <label style={styles.label}>Adressen</label>
      {["adresse1","adresse2","adresse3","adresse4","adresse5","adresse6"].map((f, i) => (
        <div key={f} style={styles.zeile}>
          <input style={styles.input} placeholder={`Adresse ${i + 1}`} value={daten[f]} onChange={(e) => setDaten({ ...daten, [f]: e.target.value })} />
          {daten[f] && <a style={styles.mapsBtn} href={mapsLink(daten[f])} target="_blank" rel="noreferrer" title="In Google Maps öffnen">🗺️</a>}
        </div>
      ))}
      <label style={styles.label}>Hotel</label>
      <div style={styles.zeile}>
        <input style={styles.input} placeholder="Hotel / Unterkunft" value={daten.hotel} onChange={(e) => setDaten({ ...daten, hotel: e.target.value })} />
        {daten.hotel && <a style={styles.mapsBtn} href={mapsLink(daten.hotel)} target="_blank" rel="noreferrer" title="In Google Maps öffnen">🗺️</a>}
      </div>
      <label style={styles.label}>Verkehrsmittel</label>
      <select style={styles.select} value={daten.verkehrsmittel} onChange={(e) => setDaten({ ...daten, verkehrsmittel: e.target.value })}>
        <option value="flug">✈️ Flug</option>
        <option value="zug">🚆 Zug</option>
        <option value="auto">🚗 Auto</option>
      </select>
      <input style={styles.input} placeholder="Hinfahrt (Verbindung / Kennzeichen / Flugnummer)" value={daten.hinfahrt} onChange={(e) => setDaten({ ...daten, hinfahrt: e.target.value })} />
      <input style={styles.input} placeholder="Rückfahrt (Verbindung / Kennzeichen / Flugnummer)" value={daten.rueckfahrt} onChange={(e) => setDaten({ ...daten, rueckfahrt: e.target.value })} />
      <label style={styles.label}>Notizen</label>
      <textarea style={styles.textarea} placeholder="Weitere Notizen..." value={daten.notizen} onChange={(e) => setDaten({ ...daten, notizen: e.target.value })} rows={4} />

      <label style={styles.label}>Screenshots</label>
      <input type="file" accept="image/*" multiple disabled={hochladen} onChange={dateiAuswahl} />
      <p style={styles.hinweis}>Tipp: Screenshot kopieren und mit Strg+V hier ins Formular einfügen.</p>
      {hochladen && <p style={styles.hinweis}>Wird hochgeladen...</p>}
      {(daten.screenshots || []).length > 0 && (
        <div style={styles.fotoGrid}>
          {daten.screenshots.map((f, i) => (
            <div key={f.pfad || i} style={styles.fotoWrapper}>
              <img src={f.url} alt="" style={styles.fotoThumb} />
              <button type="button" style={styles.fotoLoeschen} onClick={() => screenshotLoeschen(i)}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={styles.zeile}>
        <button style={styles.button} type="submit">💾 Speichern</button>
        {onAbbrechen && <button type="button" style={{ ...styles.button, backgroundColor: "#999" }} onClick={onAbbrechen}>Abbrechen</button>}
      </div>
    </form>
  );
}

export default function Reise({ user }) {
  const [reisen, setReisen] = useState([]);
  const [formular, setFormular] = useState(leerReise);
  const [offen, setOffen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDaten, setEditDaten] = useState(leerReise);

  useEffect(() => {
    const q = query(collection(db, "reisen"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => setReisen(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [user.uid]);

  const speichern = async (e) => {
    e.preventDefault();
    if (!formular.ziel.trim()) return;
    await addDoc(collection(db, "reisen"), { ...formular, uid: user.uid, erstellt: new Date() });
    setFormular(leerReise);
    setOffen(false);
  };

  const editSpeichern = async (id) => {
    await updateDoc(doc(db, "reisen", id), editDaten);
    setEditId(null);
  };

  const reiseLoeschen = async (r) => {
    await deleteDoc(doc(db, "reisen", r.id));
    for (const f of r.screenshots || []) {
      if (f?.pfad) { try { await deleteObject(ref(storage, f.pfad)); } catch { /* ggf. schon gelöscht */ } }
    }
  };

  const verkehrIcon = (v) => v === "flug" ? "✈️" : v === "zug" ? "🚆" : "🚗";

  return (
    <Seite titel="Reise">
      <button style={styles.button} onClick={() => setOffen(!offen)}>{offen ? "Abbrechen" : "+ Neue Reise"}</button>

      {offen && <ReiseFormular daten={formular} setDaten={setFormular} onSubmit={speichern} onAbbrechen={() => setOffen(false)} user={user} />}

      {reisen.length === 0 && !offen && <p style={styles.leer}>Noch keine Reisen eingetragen.</p>}
      {reisen.map((r) => (
        <div key={r.id} style={styles.karte}>
          {editId === r.id ? (
            <ReiseFormular
              daten={editDaten}
              setDaten={setEditDaten}
              onSubmit={(e) => { e.preventDefault(); editSpeichern(r.id); }}
              onAbbrechen={() => setEditId(null)}
              user={user}
              reiseId={r.id}
            />
          ) : (
            <>
              <div style={styles.karteHeader}>
                <h3 style={styles.ziel}>{r.ziel}</h3>
                <div>
                  <button style={styles.iconBtn} onClick={() => { setEditId(r.id); setEditDaten({ ...leerReise, ...r }); }}>✏️</button>
                  <button style={styles.iconBtn} onClick={() => reiseLoeschen(r)}>🗑️</button>
                </div>
              </div>
              {(r.von || r.bis) && <p style={styles.info}>📅 {r.von} – {r.bis}</p>}
              {r.hotel && (
                <p style={styles.info}>
                  🏨 {r.hotel}{" "}
                  <a href={mapsLink(r.hotel)} target="_blank" rel="noreferrer" style={styles.mapsLinkKlein} title="In Google Maps öffnen">🗺️</a>
                </p>
              )}
              {["adresse1","adresse2","adresse3","adresse4","adresse5","adresse6"].filter(f => r[f]).map((f, i) => (
                <p key={f} style={styles.info}>
                  📍 {r[f]}{" "}
                  <a href={mapsLink(r[f])} target="_blank" rel="noreferrer" style={styles.mapsLinkKlein} title="In Google Maps öffnen">🗺️</a>
                </p>
              ))}
              {(r.hinfahrt || r.rueckfahrt) && (
                <>
                  <p style={styles.info}>{verkehrIcon(r.verkehrsmittel)} Hinfahrt: {r.hinfahrt}</p>
                  <p style={styles.info}>{verkehrIcon(r.verkehrsmittel)} Rückfahrt: {r.rueckfahrt}</p>
                </>
              )}
              {r.notizen && <p style={styles.info}>📝 {r.notizen}</p>}
              {(r.screenshots || []).length > 0 && (
                <div style={styles.fotoGrid}>
                  {r.screenshots.map((f, i) => (
                    <a key={f.pfad || i} href={f.url} target="_blank" rel="noreferrer">
                      <img src={f.url} alt="" style={styles.fotoThumb} />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </Seite>
  );
}

const styles = {
  button: { padding: "0.6rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginBottom: "1rem" },
  formular: { backgroundColor: "white", padding: "1rem", borderRadius: "6px", marginBottom: "1.5rem", display: "flex", flexDirection: "column", gap: "0.6rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  zeile: { display: "flex", gap: "0.6rem" },
  label: { fontSize: "0.85rem", fontWeight: "bold", color: "#555", marginBottom: "-0.3rem" },
  input: { flex: 1, padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px" },
  select: { padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px" },
  textarea: { padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px", resize: "vertical" },
  karte: { backgroundColor: "white", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  karteHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  ziel: { margin: 0, fontSize: "1.1rem" },
  info: { margin: "0.2rem 0", color: "#444", fontSize: "0.9rem" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  leer: { color: "#999", textAlign: "center" },
  hinweis: { fontSize: "0.85rem", color: "#888", margin: 0 },
  fotoGrid: { display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.3rem" },
  fotoWrapper: { position: "relative", display: "inline-block" },
  fotoThumb: { width: "110px", height: "110px", objectFit: "cover", borderRadius: "4px" },
  fotoLoeschen: { position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", cursor: "pointer", width: "22px", height: "22px", fontSize: "0.7rem" },
  mapsBtn: { display: "flex", alignItems: "center", justifyContent: "center", width: "2.6rem", border: "1px solid #ccc", borderRadius: "4px", backgroundColor: "white", textDecoration: "none", fontSize: "1rem" },
  mapsLinkKlein: { textDecoration: "none" },
};
